import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import {
    MediaType, renditionForProfile,
    type Creative, type CreativeRendition, type DisplayProfile,
} from '@/models/media-network/Creative';
import { ALLOWED_MEDIA_TYPES } from '@/models/media-network/Playlist';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';
import { useRenditionUpload } from '../useRenditionUpload';

interface Props {
    open: boolean;
    brandId: string;
    creativeId: string | null;
    // The row exists only because an upload needs something to attach to. Cancelling therefore has to delete
    // it — otherwise backing out of "New" leaves an untitled, file-less creative in the library.
    isNew?: boolean;
    canUpdate: boolean;
    onClose: () => void;
}

const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// One creative and everything about it: its name, a slot per shape it needs, and who it was sold to.
//
// The SHAPE SLOTS are the point. A creative is not one file any more — it is the same ad rendered for each
// screen it has to play on, and the dialog shows one row per shape in the catalogue so "what is still
// missing" is visible rather than something the user has to work out from a file list.
const CreativeDialog = ({ open, brandId, creativeId, isNew, canUpdate, onClose }: Props) => {
    const strings = useLocalizedStrings();
    const toast = useToast();
    const qc = useQueryClient();

    const { upload, phaseLabel, progress, busy, error, setError } = useRenditionUpload();

    const [name, setName] = useState('');
    const [advertiserId, setAdvertiserId] = useState('');
    const [assetCode, setAssetCode] = useState('');
    const [tagIds, setTagIds] = useState<string[]>([]);

    // Which rendition an incoming file replaces. Null = a new one.
    const replacingRef = useRef<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const { data: creative } = useQuery({
        queryKey: mediaNetworkKeys.creative(creativeId ?? ''),
        enabled: !!creativeId && open,
        queryFn: () => mediaNetworkApi.getCreative(creativeId!),
    });

    const { data: profiles = [] } = useQuery({
        queryKey: mediaNetworkKeys.displayProfiles(),
        enabled: open,
        queryFn: () => mediaNetworkApi.listDisplayProfiles(),
        staleTime: Infinity,
    });

    const { data: advertisers = [] } = useQuery({
        queryKey: mediaNetworkKeys.advertisers(brandId),
        enabled: open,
        queryFn: () => mediaNetworkApi.listAdvertisers(brandId),
    });

    const { data: tags = [] } = useQuery({
        queryKey: mediaNetworkKeys.tags(brandId),
        enabled: open,
        queryFn: () => mediaNetworkApi.listTags(brandId),
    });

    // Which creative the form below currently holds. Seeding is keyed on the ID, NOT on the creative object:
    // uploading refetches it, and re-seeding there would throw away a name the user had typed but not saved.
    const seededRef = useRef<string | null>(null);

    useEffect(() => {
        if (!open) { seededRef.current = null; return; }
        if (!creative || seededRef.current === creative.id) return;

        seededRef.current = creative.id;
        // A brand-new creative carries a placeholder name that exists only to satisfy the API. Showing it
        // would invite saving it, so start empty instead — Save stays disabled until it is named.
        setName(isNew ? '' : creative.name);
        setAdvertiserId(creative.advertiserId ?? '');
        setAssetCode(creative.advertiserAssetCode ?? '');
        setTagIds(creative.tagIds);
    }, [open, creative, isNew]);

    const refresh = async () => {
        await qc.invalidateQueries({ queryKey: mediaNetworkKeys.creative(creativeId ?? '') });
        await qc.invalidateQueries({ queryKey: mediaNetworkKeys.creatives(brandId) });
        // A tag's usage count moves when a creative's tags do.
        await qc.invalidateQueries({ queryKey: mediaNetworkKeys.tags(brandId) });
        await qc.invalidateQueries({ queryKey: mediaNetworkKeys.advertisers(brandId) });
        // A playlist shows its creatives inline, so it is stale too.
        await qc.invalidateQueries({ queryKey: mediaNetworkKeys.list(brandId) });
    };

    const { mutateAsync: save, isPending: isSaving } = useMutation({
        mutationFn: async () => {
            if (!creativeId) return;
            if (name.trim() && name.trim() !== creative?.name)
                await mediaNetworkApi.renameCreative(creativeId, name.trim());

            await mediaNetworkApi.setAttribution(creativeId, {
                advertiserId: advertiserId || null,
                advertiserAssetCode: advertiserId ? (assetCode.trim() || null) : null,
                tagIds,
            });
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.creativeSaved });
            await refresh();
            onClose();
        },
        onError: () => toast({ severity: 'error', message: strings.creativeSaveError }),
    });

    // Cancel on a creative that only exists so an upload had a home. Any renditions already uploaded go with
    // it — the backend deletes their blobs — which is what cancelling a creation should mean.
    const { mutateAsync: discard, isPending: isDiscarding } = useMutation({
        mutationFn: () => mediaNetworkApi.deleteCreative(creativeId!),
        onSettled: async () => {
            // Even on failure: the row is real, so the list has to show whatever is actually there.
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.creatives(brandId) });
            onClose();
        },
        onError: () => toast({ severity: 'error', message: strings.creativeDeleteError }),
    });

    const close = () => (isNew ? void discard() : onClose());

    const { mutateAsync: removeRendition } = useMutation({
        mutationFn: (renditionId: string) => mediaNetworkApi.deleteRendition(creativeId!, renditionId),
        onSuccess: refresh,
        onError: () => toast({ severity: 'error', message: strings.renditionDeleteError }),
    });

    const onFilePicked = async (file: File | undefined) => {
        if (!file || !creativeId) return;
        const replacing = replacingRef.current;
        replacingRef.current = null;

        const ok = await upload(creativeId, file, replacing ?? undefined);
        if (ok) {
            toast({ severity: 'success', message: replacing ? strings.renditionReplaced : strings.renditionAdded });
            await refresh();
        }
    };

    const pickFile = (replacesRenditionId?: string) => {
        replacingRef.current = replacesRenditionId ?? null;
        setError(null);
        fileRef.current?.click();
    };

    if (!creativeId) return null;

    // Renditions that match no profile in the catalogue. Should be impossible — commit hard-blocks an unknown
    // shape — but showing them beats a file silently vanishing from the dialog if a profile is ever retired.
    const matched = new Set(
        profiles.map((p) => renditionForProfile(creative ?? { renditions: [] } as unknown as Creative, p)?.id)
            .filter(Boolean) as string[]);
    const unmatched = (creative?.renditions ?? []).filter((r) => !matched.has(r.id));

    return (
        <Dialog open={open} onClose={busy || isDiscarding ? undefined : close} maxWidth="md" fullWidth>
            <DialogTitle>{strings.creative}</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField
                        label={strings.name}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={!canUpdate || busy}
                        fullWidth
                    />

                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    {busy && (
                        <Box>
                            <Typography variant="body2" color="text.secondary">{phaseLabel}</Typography>
                            <LinearProgress variant="determinate" value={progress} />
                        </Box>
                    )}

                    <Divider />

                    <Typography variant="subtitle2">{strings.renditions}</Typography>
                    {/* Said once here rather than only when an upload is refused — the shape rule is easier to
                        follow than to discover. */}
                    <Alert severity="info" variant="outlined">{strings.renditionsHelp}</Alert>

                    {profiles.map((profile) => (
                        <RenditionRow
                            key={profile.id}
                            profile={profile}
                            rendition={creative ? renditionForProfile(creative, profile) : undefined}
                            canUpdate={canUpdate}
                            busy={busy}
                            onAdd={() => pickFile()}
                            onReplace={(id) => pickFile(id)}
                            onDelete={(id) => removeRendition(id)}
                            strings={strings}
                        />
                    ))}

                    {unmatched.length > 0 && (
                        <Alert severity="warning" variant="outlined">
                            {strings.renditionsUnmatched.replace('{{count}}', String(unmatched.length))}
                        </Alert>
                    )}

                    <Divider />

                    <Typography variant="subtitle2">{strings.playlistAttribution}</Typography>

                    <FormControl fullWidth>
                        <InputLabel id="advertiser-label">{strings.playlistAdvertiser}</InputLabel>
                        <Select
                            labelId="advertiser-label"
                            label={strings.playlistAdvertiser}
                            value={advertiserId}
                            onChange={(e) => setAdvertiserId(e.target.value)}
                            disabled={!canUpdate || busy}
                        >
                            <MenuItem value="">{strings.playlistNoAdvertiser}</MenuItem>
                            {/* A disabled advertiser stays listed while it is the current selection —
                                otherwise editing an old creative would silently drop its attribution. */}
                            {advertisers
                                .filter((a) => a.enabled || a.id === advertiserId)
                                .map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <TextField
                        label={strings.playlistAssetCode}
                        helperText={strings.playlistAssetCodeHelp}
                        value={assetCode}
                        onChange={(e) => setAssetCode(e.target.value)}
                        // Names one of THEIR creatives, so it means nothing without an advertiser. The
                        // backend clears it too, so this is not the only guard.
                        disabled={!canUpdate || busy || !advertiserId}
                        fullWidth
                    />

                    <FormControl fullWidth>
                        <InputLabel id="tags-label">{strings.playlistItemTags}</InputLabel>
                        <Select
                            labelId="tags-label"
                            label={strings.playlistItemTags}
                            multiple
                            value={tagIds}
                            onChange={(e) => setTagIds(typeof e.target.value === 'string' ? [] : e.target.value)}
                            disabled={!canUpdate || busy}
                            renderValue={(selected) => (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                    {(selected as string[]).map((id) => (
                                        <Chip key={id} size="small" label={tags.find((t) => t.id === id)?.name ?? id} />
                                    ))}
                                </Stack>
                            )}
                        >
                            {tags.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={close} loading={isDiscarding} disabled={busy}>
                    {strings.cancel}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => save()}
                    loading={isSaving}
                    disabled={!canUpdate || busy || isSaving || isDiscarding || !name.trim()}
                >
                    {strings.save}
                </Button>
            </DialogActions>

            <input
                ref={fileRef}
                type="file"
                hidden
                accept={ALLOWED_MEDIA_TYPES.join(',')}
                onChange={(e) => {
                    void onFilePicked(e.target.files?.[0]);
                    // Cleared so picking the same file twice in a row still fires onChange.
                    e.target.value = '';
                }}
            />
        </Dialog>
    );
};

// One shape, and whether this creative fills it. An empty slot is the useful state — it is the thing the
// user has to go and do something about.
const RenditionRow = ({
    profile, rendition, canUpdate, busy, onAdd, onReplace, onDelete, strings,
}: {
    profile: DisplayProfile;
    rendition?: CreativeRendition;
    canUpdate: boolean;
    busy: boolean;
    onAdd: () => void;
    onReplace: (renditionId: string) => void;
    onDelete: (renditionId: string) => void;
    strings: ReturnType<typeof useLocalizedStrings>;
}) => (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={2} alignItems="center">
            <Stack sx={{ minWidth: 200 }}>
                <Typography variant="body2" fontWeight={500}>{profile.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {profile.display}
                    {profile.minWidth ? ` · ${strings.renditionMin} ${profile.minWidth}x${profile.minHeight}` : ''}
                </Typography>
            </Stack>

            {rendition ? (
                <>
                    {rendition.mediaType === MediaType.Video
                        ? <VideocamIcon fontSize="small" color="action" />
                        : <ImageIcon fontSize="small" color="action" />}
                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{rendition.fileName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {rendition.width}x{rendition.height} · {formatBytes(rendition.sizeBytes)}
                        </Typography>
                    </Stack>
                    {canUpdate && (
                        <>
                            <IconButton size="small" disabled={busy} onClick={() => onReplace(rendition.id)}
                                aria-label="replace rendition">
                                <SwapHorizIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" disabled={busy} onClick={() => onDelete(rendition.id)}
                                aria-label="delete rendition">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </>
                    )}
                </>
            ) : (
                <>
                    <Chip size="small" variant="outlined" color="warning" label={strings.renditionMissing} />
                    <Box sx={{ flex: 1 }} />
                    {canUpdate && (
                        <Button size="input" variant="outlined" startIcon={<AddIcon />} disabled={busy} onClick={onAdd}>
                            {strings.renditionUpload}
                        </Button>
                    )}
                </>
            )}
        </Stack>
    </Paper>
);

export default CreativeDialog;
